// Due Date Control's FILTERS (Aaron 2026-10-21, Synergy: "Synergy need to be able to filter
// records and we need a quick filter as there are groups of records for different rolls").
//
// Every column the list shows filters in the shared Filters panel (Component 73), on both modes
// (Due Dates and Templates are one grid, DueDateDataGrid `view`). The panel's items reach GET
// /v6/duedate as the generic route's `filter` specs (DataGrid.tsx → transformDueDateListParams →
// app/filtering.py + sqlalchemy-filters), which filter on the WhiteBoard MODEL's columns:
//
//   column (grid field)   filter                       reaches the route as
//   Title / Reference /   contains · equals ·          ilike / equals on the column
//   Comment               starts with · ends with
//   Section (section)     pick-list: types under       section_id   (FILTER_COLUMN below)
//                         whiteboard_sections_id
//   Type (type_id)        types under whiteboard_type  type_id
//   Company (company)     the viewer's companies       company_id   (FILTER_COLUMN below)
//   Department            types under whiteboard_      department; the Quick Filters chips
//                         department_parent_id         ("is any of" → IN)
//   Assigned To           a user (Default Filters)     the route's own `assigned_to` param, which
//                                                      also matches the record's recipients
//   Last Done / Due Date  the tenant date picker       last_done / required
//
// The pick-lists come from the tenant's config, so every tenant gets its own sections, types and
// departments; a family the tenant has not configured has no options (and no chips).
import { useMemo } from "react";

import { useTypeOptions } from "../hooks/useTypeOptions";
import { useUserCompanyOptions } from "../hooks/useUserCompanyOptions";
import type { ListStatusOption } from "../components/datagrid/filterPanel";

/**
 * A grid field whose filter is on another WhiteBoard column: the cell shows a joined name
 * (`section.name`, `company_obj.name`), the route filters the id. The field itself stays, so a
 * viewer's saved column layout is kept.
 */
export const FILTER_COLUMN: Readonly<Record<string, string>> = {
  section: "section_id",
  company: "company_id",
};

/** The Assigned To column: its "is" item goes as the route's `assigned_to` param. */
export const ASSIGNEE_FIELD = "assigned_to";

/** The Department column: the Quick Filters row's field. */
export const DEPARTMENT_FIELD = "department";

/**
 * A whiteboard `*_parent_id` config value as a types parent id: a number, a numeric string, or
 * the config editor's "Name (123)"; anything else (unset, a map) is none.
 */
export function whiteboardTypeParentId(raw: unknown): number | undefined {
  if (raw == null || raw === "" || typeof raw === "object") return undefined;
  if (typeof raw === "string") {
    const parenMatch = raw.match(/\((\d+)\)\s*$/);
    if (parenMatch) return Number(parenMatch[1]);
  }
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const asOptions = (options: { value: unknown; label?: string }[] | undefined): ListStatusOption[] =>
  (options ?? [])
    .filter((o) => o.value != null && o.value !== "")
    .map((o) => ({ value: String(o.value), label: String(o.label ?? o.value) }));

/** The tenant's pick-lists for the list's filters (no "Any" row: an empty filter is All). */
export function useWhiteboardFilterOptions(config: Record<string, any> | undefined) {
  const sections = useTypeOptions(whiteboardTypeParentId(config?.sections_id));
  const types = useTypeOptions(whiteboardTypeParentId(config?.type));
  const departments = useTypeOptions(whiteboardTypeParentId(config?.department_parent_id));
  const companies = useUserCompanyOptions();
  return useMemo(
    () => ({
      sections: asOptions(sections as any),
      types: asOptions(types as any),
      departments: asOptions(departments as any),
      companies: asOptions(companies as any),
    }),
    [sections, types, departments, companies]
  );
}
